/**
 * A flat, character-addressable view of one Word paragraph.
 *
 * Word stores paragraph text as runs (`w:r`) that may sit inside tracked
 * insertions and deletions (`w:ins`, `w:del`, `w:moveTo`, `w:moveFrom`),
 * content controls, hyperlinks and other containers, nested to any depth. The
 * engine needs to ask "which run holds character 412, who inserted it, and may
 * I edit it?". `ParagraphModel` answers that by walking the paragraph once and
 * emitting one `TextPiece` per text node, each carrying the authorship it
 * inherits from the revision containers around it.
 *
 * Offsets are expressed in accepted text: the text a reader sees once every
 * tracked change is accepted. Deleted pieces therefore have zero width and sit
 * at the offset where their deletion happened. That is exactly the coordinate
 * system the requested text is written in, so the diff and the DOM agree.
 *
 * The model is also the one reading of "what does this paragraph say" that
 * the rest of the redline code trusts: the proposal's accepted texts, the
 * text a paragraph had before one author touched it, the bookmark offsets and
 * the web route's paragraph list all come from these pieces, never from a
 * second walker that could disagree with the engine.
 */
import {
    COMMENT_REFERENCE_TAG,
    FIELD_CHAR_TAG,
    FIELD_INSTRUCTION_TAGS,
    fieldResultIsRedrawnByWord,
    fieldSimpleInstructionOf,
    GLYPH_CHARS,
    DELETION_CONTAINERS,
    INLINE_OBJECT_TAGS,
    INSERTION_CONTAINERS,
    isGlyphTag,
    elementNameWithoutPrefix,
    OPAQUE_TAGS,
    PROPERTY_TAGS,
    TEXT_TAGS,
    TOGGLE_OFF_VALUES,
    WORDPROCESSINGML_NS,
    wordIdOf,
} from "./ooxml-names";
import { Revision } from "./revision";
import { complexFieldWalkOf, type FieldOpenOverRun } from "./word-owned-text-unlinker";

interface TextPieceConfig {
    textNode: Element;
    insertedBy: string | null;
    deletedBy: string | null;
    insertionContainer: Element | null;
    deletionContainer: Element | null;
    editable: boolean;
    formatting: RunFormatting;
}

/**
 * The two manual character formats legal drafting conventions carry meaning
 * in (USPTO amendments strike deletions and underline insertions without
 * tracked changes). Read from the run's `w:rPr`; shown in the agent view only.
 */
export interface RunFormatting {
    struck: boolean;
    underlined: boolean;
}

const PLAIN_FORMATTING: RunFormatting = { struck: false, underlined: false };

/**
 * A picture or embedded object inside the paragraph, remembered by the piece
 * it precedes so the agent view can show `<image/>` where it sits. It claims
 * no accepted characters, so offsets and the engine never see it.
 */
interface InlineObjectMarker {
    /** Index into the pieces list of the first piece after the object; `pieces.length` when it closes the paragraph. */
    beforePieceIndex: number;
    insertedBy: string | null;
    deletedBy: string | null;
}

/**
 * One text node of the paragraph with the facts the engine needs about it.
 *
 * A piece owns its two mutations: the splicer strikes text by asking the piece
 * to `markDeletedBy` the revision's author, and swaps `w:t` for `w:delText`
 * (or back) by asking it to `replaceTextNode`. Offsets are written only by the
 * model's `refresh`; between a mutation and the next refresh they describe the
 * paragraph as it was when the edit was planned, which is what a back-to-front
 * application of planned edits relies on.
 */
export class TextPiece {
    private node: Element;
    /** Offsets in accepted text; `startOffset === endOffset` for a deleted piece. Written by `ParagraphModel.refresh`. */
    startOffset = 0;
    endOffset = 0;
    /** Author of the outermost insertion this piece sits in; null for original text. */
    readonly insertedBy: string | null;
    /** Author of the outermost deletion this piece sits in; null while the text is live. */
    private deletedByAuthor: string | null;
    /**
     * The outermost insertion container (`w:ins`, `w:moveTo`) this piece sits in: the
     * element whose `w:author` is `insertedBy` and whose `w:date` says when it was written.
     *
     * NULL
     *   Original text, which no insertion holds.
     */
    readonly insertionContainer: Element | null;
    /**
     * The outermost deletion container (`w:del`, `w:moveFrom`) this piece sits in, whose
     * `w:author` is `deletedBy`.
     *
     * NULL
     *   Live text, or a piece `markDeletedBy` struck during this edit pass: the splicer
     *   writes that container and the next refresh reads it.
     */
    readonly deletionContainer: Element | null;
    /** False inside field results and field instructions, which Word redraws from its own markup. */
    readonly editable: boolean;
    /** Strike-through and underline read from the run's properties; the agent view marks them, nothing else reads them. */
    readonly formatting: RunFormatting;

    constructor(config: TextPieceConfig) {
        this.node = config.textNode;
        this.insertedBy = config.insertedBy;
        this.deletedByAuthor = config.deletedBy;
        this.insertionContainer = config.insertionContainer;
        this.deletionContainer = config.deletionContainer;
        this.editable = config.editable;
        this.formatting = config.formatting;
    }

    /** The `w:t`, `w:delText`, `w:tab`, `w:br` or `w:cr` element holding the characters. */
    get textNode(): Element {
        return this.node;
    }

    /**
     * The run the text node sits in: its parent for ordinary text, and the run
     * above the revision container for a tracked change written inside a run,
     * which is how Word writes one inside an equation
     * (`<m:r><w:del><m:t>…</m:t></w:del></m:r>`).
     */
    get run(): Element {
        for (let ancestor: Element | null = this.node.parentElement; ancestor; ancestor = ancestor.parentElement) {
            if (elementNameWithoutPrefix(ancestor) === "r") return ancestor;
        }
        return this.node.parentElement as Element;
    }

    get deletedBy(): string | null {
        return this.deletedByAuthor;
    }

    get isDeleted(): boolean {
        return this.deletedByAuthor !== null;
    }

    /** True for `w:tab`, `w:br` and `w:cr`, whose text is a single character the element stands for. */
    get isGlyph(): boolean {
        return TextPiece.isGlyphNode(this.node);
    }

    /** The text this piece contributes: the glyph character for a glyph element, otherwise the node's text content. */
    get text(): string {
        return TextPiece.textOf(this.node);
    }

    /** Whether a raw element is one of the glyph elements; used on nodes that are not yet pieces. */
    static isGlyphNode(node: Element): boolean {
        return isGlyphTag(elementNameWithoutPrefix(node));
    }

    /** The text a raw element contributes, by the same rule as `text`; used on nodes that are not yet pieces. */
    static textOf(node: Element): string {
        const tagName = elementNameWithoutPrefix(node);
        /**
         * CASES
         *   - glyph element       -> mapped character from `GLYPH_CHARS`
         *   - other text element  -> `node.textContent`
         *   - missing text        -> `""`, so an empty `w:t` adds nothing
         */
        return isGlyphTag(tagName) ? GLYPH_CHARS[tagName] : node.textContent ?? "";
    }

    /**
     * Records that this piece's text was struck (or vacated) by `author`. The
     * piece leaves the accepted text at once, so a later step in the same edit
     * pass never treats it as live; its offsets stay as planned until the model
     * refreshes.
     */
    markDeletedBy(author: string): void {
        this.deletedByAuthor = author;
    }

    /** Points the piece at the element that now carries its characters, after the splicer swapped `w:t` and `w:delText`. */
    replaceTextNode(node: Element): void {
        this.node = node;
    }
}

export class ParagraphModel {
    readonly paragraph: Element;
    private allChangesAcceptedText = "";
    private textPieces: TextPiece[] = [];
    private inlineObjects: InlineObjectMarker[] = [];
    private commentReferences: string[] = [];

    /** Walks the paragraph once; `refresh` re-walks it after the splicer changed the DOM. */
    constructor(paragraph: Element) {
        this.paragraph = paragraph;
        this.refresh();
    }

    /** The accepted text: every live piece's text in document order, deleted text left out. */
    get text(): string {
        return this.allChangesAcceptedText;
    }

    /** Every piece in document order, deleted ones included; `restoreOwnDeletions` reads those. */
    get pieces(): readonly TextPiece[] {
        return this.textPieces;
    }

    /** The `w:id` of every comment anchored in this paragraph, in the order its `w:commentReference` runs appear, nested containers included. */
    get commentReferenceIds(): readonly string[] {
        return this.commentReferences;
    }

    /**
     * The text with every tracked change rejected: insertions by anyone
     * dropped, deletions by anyone kept. This is the paragraph as it read
     * before any of the revisions it carries, the second layer the paragraph
     * locator searches when a paragraph's text was struck rather than edited.
     */
    get allChangesRejectedText(): string {
        return this.textPieces
            .filter((piece) => piece.insertedBy === null)
            .map((piece) => piece.text)
            .join("");
    }

    /**
     * Rebuilds the pieces from the DOM. Authorship is inherited from the
     * outermost revision container: text inserted by B inside an insertion by
     * A belongs to A, because accepting or rejecting A's insertion decides its
     * fate. Property blocks are skipped so placeholder captions and style names
     * never leak into the paragraph text.
     *
     * Field text is marked uneditable in both spellings Word uses. The nested
     * spelling (`w:fldSimple`, `m:oMath`) is an ancestor of its runs. The flat
     * spelling that Word writes for DATE, REF, PAGEREF and table-of-contents
     * entries is a run sequence `fldChar begin` / `instrText` / `fldChar
     * separate` / result runs / `fldChar end` with no container at all, so the
     * one field walk (`complexFieldWalkOf`) says which fields are open over each
     * run, and every piece of a run inside a field Word redraws is marked.
     * Without that the engine wrote tracked changes into a cross-reference's
     * result, and Word discarded them on the next field update.
     *
     * EDGE CASE
     *   A HYPERLINK field's result is the link's display text, which Word keeps
     *   when fields update (`fieldResultIsRedrawnByWord`), so it stays editable in
     *   both spellings. Marked uneditable, every edit of a field-form link unlinked
     *   it: the engine dropped its markers and the link was lost.
     */
    refresh(): void {
        const hasOpaqueAncestor = (run: Element): boolean => {
            for (let node: Element | null = run; node; node = node.parentElement) {
                /**
                 * WHEN the run sits inside an opaque container whose field Word redraws
                 *   CONSEQUENCE
                 *     return true.
                 *     Text there is treated as uneditable because Word regenerates it from markup;
                 *     a `w:fldSimple` HYPERLINK is the author's text and reads as editable.
                 */
                if (OPAQUE_TAGS.has(elementNameWithoutPrefix(node)) && fieldResultIsRedrawnByWord(fieldSimpleInstructionOf(node))) return true;
            }
            return false;
        };
        /** The paragraph's complex fields, read once: which fields are open over each run decides whether its text is editable. */
        const fieldWalk = complexFieldWalkOf(this.paragraph);

        const pieces: TextPiece[] = [];
        const inlineObjects: InlineObjectMarker[] = [];
        const commentReferences: string[] = [];
        let cursor = 0;
        const walk = (node: Element, insertedBy: string | null, deletedBy: string | null, insertionContainer: Element | null, deletionContainer: Element | null): void => {
            for (const child of Array.from(node.children)) {
                const tag = elementNameWithoutPrefix(child);
                /**
                 * WHEN the child is a property block
                 *   CONSEQUENCE
                 *     skip it.
                 *     Style metadata and placeholder labels never become paragraph text.
                 */
                if (PROPERTY_TAGS.has(tag)) continue;
                /**
                 * WHEN entering a tracked insertion
                 *   CONSEQUENCE
                 *     walk the subtree with the first insertion author on the path.
                 * CASES
                 *   - `insertedBy` already set  -> keep the outer author and the outer container
                 *   - `insertedBy` is null      -> take this container's author, and this container
                 */
                if (INSERTION_CONTAINERS.has(tag)) {
                    const insertionAuthorFromContainer = child.getAttributeNS(WORDPROCESSINGML_NS, "author");
                    const insertedByAuthor = insertedBy ?? insertionAuthorFromContainer ?? "";
                    walk(child, insertedByAuthor, deletedBy, insertionContainer ?? child, deletionContainer);
                    /**
                     * WHEN the insertion subtree was already walked with insertion authorship
                     *   CONSEQUENCE
                     *     stop the generic child walk here.
                     */
                    continue;
                }
                /**
                 * WHEN entering a tracked deletion
                 *   CONSEQUENCE
                 *     walk the subtree with the first deletion author on the path.
                 * CASES
                 *   - `deletedBy` already set  -> keep the outer author and the outer container
                 *   - `deletedBy` is null      -> take this container's author, and this container
                 */
                if (DELETION_CONTAINERS.has(tag)) {
                    const deletionAuthorFromContainer = child.getAttributeNS(WORDPROCESSINGML_NS, "author");
                    const deletedByAuthor = deletedBy ?? deletionAuthorFromContainer ?? "";
                    walk(child, insertedBy, deletedByAuthor, insertionContainer, deletionContainer ?? child);
                    /**
                     * WHEN the deletion subtree was already walked with deletion authorship
                     *   CONSEQUENCE
                     *     stop the generic child walk here.
                     */
                    continue;
                }
                /**
                 * WHEN the child is a run
                 *   CONSEQUENCE
                 *     handle it here.
                 * WHY
                 *   Only runs can contribute field markers or text pieces.
                 */
                if (tag === "r") {
                    emitRunContent(child, child, insertedBy, deletedBy, insertionContainer, deletionContainer);
                    /**
                     * WHEN the run's children were already handled in run-specific order
                     *   CONSEQUENCE
                     *     do not descend into the run again.
                     */
                    continue;
                }
                walk(child, insertedBy, deletedBy, insertionContainer, deletionContainer);
            }
        };
        /**
         * Emits the text pieces of one run's content. `run` is the run itself,
         * `contentParent` the element whose children are being read: the run, or
         * a revision container inside it.
         *
         * WHY the two differ
         *   A tracked change inside an equation is written as
         *   `<m:r><w:del …><m:t>…</m:t></w:del></m:r>`: the container sits inside
         *   the run, not around it, which is the spelling Word writes and the one
         *   the splicer writes back. Reading only the run's direct children lost
         *   every character of such a change.
         */
        const emitRunContent = (
            run: Element,
            contentParent: Element,
            insertedBy: string | null,
            deletedBy: string | null,
            insertionContainer: Element | null,
            deletionContainer: Element | null,
        ): void => {
                    const insideOpaqueContainer = hasOpaqueAncestor(run);
                    /**
                     * The complex fields open over this run, outermost first; none for a run outside every field.
                     *
                     * EXAMPLE
                     *   a run inside a HYPERLINK's result                 -> [{ the HYPERLINK, in its result }]
                     *   a contents entry's link text                      -> [{ the TOC, in its result }, { the HYPERLINK, in its result }]
                     */
                    const fieldsOpenOverRun = fieldWalk.fieldsOpenOverRun.get(run) ?? [];
                    const formatting = ParagraphModel.runFormatting(run);
                    for (const inner of Array.from(contentParent.children)) {
                        const innerTag = elementNameWithoutPrefix(inner);
                        /**
                         * WHEN the run child is a tracked insertion around this run's text
                         *   CONSEQUENCE
                         *     read its text with the first insertion author on the path, the same rule
                         *     the paragraph-level walk uses for a container around a whole run.
                         */
                        if (INSERTION_CONTAINERS.has(innerTag)) {
                            const insertionAuthorFromContainer = inner.getAttributeNS(WORDPROCESSINGML_NS, "author");
                            emitRunContent(run, inner, insertedBy ?? insertionAuthorFromContainer ?? "", deletedBy, insertionContainer ?? inner, deletionContainer);
                            continue;
                        }
                        /**
                         * WHEN the run child is a tracked deletion around this run's text
                         *   CONSEQUENCE
                         *     read its text as deleted by the first deletion author on the path.
                         */
                        if (DELETION_CONTAINERS.has(innerTag)) {
                            const deletionAuthorFromContainer = inner.getAttributeNS(WORDPROCESSINGML_NS, "author");
                            emitRunContent(run, inner, insertedBy, deletedBy ?? deletionAuthorFromContainer ?? "", insertionContainer, deletionContainer ?? inner);
                            continue;
                        }
                        /**
                         * WHEN the run child is, or wraps, a picture or embedded object
                         *   CONSEQUENCE
                         *     remember it before the next piece, for the agent view; no text piece.
                         * WHY
                         *   The model must know something unreadable sits there; the engine must not.
                         */
                        if (ParagraphModel.holdsInlineObject(inner)) {
                            inlineObjects.push({ beforePieceIndex: pieces.length, insertedBy, deletedBy });
                            continue;
                        }
                        /**
                         * WHEN the run child anchors a comment
                         *   CONSEQUENCE
                         *     remember the comment's id; no text piece.
                         * WHY
                         *   The reader route lists a paragraph's comments by these ids; the marker itself is not text.
                         */
                        if (elementNameWithoutPrefix(inner) === COMMENT_REFERENCE_TAG) {
                            const commentReferenceId = wordIdOf(inner);
                            if (commentReferenceId !== null) commentReferences.push(commentReferenceId);
                            continue;
                        }
                        /**
                         * WHEN the run child is a field marker (`w:fldChar`) or a field's instruction
                         *      (`w:instrText`, or `w:delInstrText` struck)
                         *   CONSEQUENCE
                         *     no text piece: it is control markup, not paragraph characters, read by the
                         *     one field walk (`complexFieldWalkOf`) that decides the editability of the
                         *     run's text.
                         */
                        if (innerTag === FIELD_CHAR_TAG || FIELD_INSTRUCTION_TAGS.has(innerTag)) continue;
                        const innerIsTextTag = TEXT_TAGS.has(elementNameWithoutPrefix(inner));
                        const innerIsGlyphNode = TextPiece.isGlyphNode(inner);
                        const innerProducesTextPiece = innerIsTextTag || innerIsGlyphNode;
                        /**
                         * WHEN the run child is neither a text element nor a glyph element
                         *   CONSEQUENCE
                         *     skip it.
                         * WHY
                         *   Drawings, properties and bookmarks do not become text pieces.
                         */
                        if (!innerProducesTextPiece) continue;
                        const length = TextPiece.textOf(inner).length;
                        /**
                         * WHEN the text contribution is empty
                         *   CONSEQUENCE
                         *     skip it.
                         * WHY
                         *   A zero-length node claims no character position and cannot anchor an edit.
                         */
                        if (length === 0) continue;
                        const piece = new TextPiece({
                            textNode: inner,
                            insertedBy,
                            deletedBy,
                            insertionContainer,
                            deletionContainer,
                            editable: !insideOpaqueContainer && fieldsOpenOverRun.every(ParagraphModel.fieldResultIsAuthorText),
                            formatting,
                        });
                        piece.startOffset = cursor;
                        piece.endOffset = cursor + length;
                        pieces.push(piece);
                        cursor += length;
                    }
        };
        walk(this.paragraph, null, null, null, null);

        // Re-base every piece onto accepted-text coordinates. The walk counted
        // deleted characters too; here a deleted piece collapses to a zero-width
        // point at the offset its deletion occupies, so it still says where it is
        // while claiming no accepted characters.
        let accepted = 0;
        for (const piece of pieces) {
            const length = piece.endOffset - piece.startOffset;
            piece.startOffset = accepted;
            /**
             * CASES
             *   - deleted piece -> `endOffset = accepted`
             *   - live piece    -> `endOffset = accepted + length`
             *
             * EXAMPLE
             *   `accepted` = 10
             *   `length`   = 4
             *   deleted    -> `endOffset = 10`
             *   live       -> `endOffset = 14`
             */
            piece.endOffset = piece.isDeleted ? accepted : accepted + length;
            /**
             * WHEN the piece is live
             *   CONSEQUENCE
             *     advance `accepted` by `length`.
             * WHY
             *   Deleted characters occupy a point in accepted coordinates but add no width.
             */
            if (!piece.isDeleted) accepted += length;
        }

        this.textPieces = pieces;
        this.inlineObjects = inlineObjects;
        this.commentReferences = commentReferences;
        this.allChangesAcceptedText = pieces
            .filter((piece) => !piece.isDeleted)
            .map((piece) => piece.text)
            .join("");
    }

    /**
     * Strike-through (`w:strike` or `w:dstrike`) and underline (`w:u` with a
     * value other than none) from the run's own properties. A toggle whose
     * `w:val` spells "off" counts as off, the way Word reads it.
     */
    /**
     * Whether a field the walk is inside leaves the text in its result the author's:
     * true once it is in its result and its type is one Word does not redraw.
     *
     * CASES
     *   - a HYPERLINK in its result                   -> true: the display text is edited in place
     *   - a REF, PAGEREF, TOC, DATE… in its result     -> false: Word redraws it
     *   - any field still in its instruction part      -> false: no result text yet
     *
     * EXAMPLE
     *   open fields [HYPERLINK in its result]                 -> every one true  -> the piece is editable
     *   open fields [TOC in its result, HYPERLINK in result]  -> the TOC false   -> uneditable, the contents entry is Word's
     */
    private static fieldResultIsAuthorText(openField: FieldOpenOverRun): boolean {
        return openField.inResult && !fieldResultIsRedrawnByWord(openField.field.fieldInstruction);
    }

    private static runFormatting(run: Element): RunFormatting {
        const runProperties = Array.from(run.children).find((child) => elementNameWithoutPrefix(child) === "rPr");
        /**
         * WHEN the run has no properties block
         *   CONSEQUENCE
         *     plain text.
         */
        if (runProperties === undefined) return PLAIN_FORMATTING;
        const toggleIsOn = (tagName: string): boolean => {
            const toggle = Array.from(runProperties.children).find((child) => elementNameWithoutPrefix(child) === tagName);
            /**
             * CASES
             *   - element absent            -> off
             *   - `w:val` absent            -> on (a bare `<w:strike/>` means on)
             *   - `w:val` spells "off"      -> off
             *   - any other value           -> on
             */
            if (toggle === undefined) return false;
            const value = toggle.getAttributeNS(WORDPROCESSINGML_NS, "val") ?? toggle.getAttribute("w:val");
            return value === null || !TOGGLE_OFF_VALUES.has(value);
        };
        const underline = Array.from(runProperties.children).find((child) => elementNameWithoutPrefix(child) === "u");
        /**
         * CASES
         *   - no `w:u`                 -> null, read as "not underlined" below
         *   - `w:u` present            -> its `w:val` (the underline style), null when it carries none
         */
        const underlineValue = underline === undefined ? null : (underline.getAttributeNS(WORDPROCESSINGML_NS, "val") ?? underline.getAttribute("w:val"));
        return {
            struck: toggleIsOn("strike") || toggleIsOn("dstrike"),
            /**
             * CASES
             *   - no `w:u`                 -> not underlined
             *   - `w:u` without a value    -> not underlined (Word needs a style)
             *   - `w:u w:val="none"`       -> not underlined
             *   - any other style          -> underlined
             */
            underlined: underline !== undefined && underlineValue !== null && underlineValue !== "none",
        };
    }

    /** Whether a run child is a picture or embedded object, or wraps one (an alternate-content block around a drawing). */
    private static holdsInlineObject(runChild: Element): boolean {
        /**
         * WHEN the child itself is a drawing, object or picture
         *   CONSEQUENCE
         *     it is an inline object.
         */
        if (INLINE_OBJECT_TAGS.has(elementNameWithoutPrefix(runChild))) return true;
        return Array.from(runChild.getElementsByTagName("*")).some((descendant) => INLINE_OBJECT_TAGS.has(elementNameWithoutPrefix(descendant)));
    }

    /**
     * Pieces that hold live characters, in document order. Deleted pieces are
     * left out because they claim no accepted characters, and so are pieces of
     * zero width, which can only be an empty text node that survived the walk;
     * neither can be the home of an edit. `restoreOwnDeletions` reads `pieces`
     * instead, because the deleted pieces are exactly what it is looking for.
     */
    acceptedPieces(): TextPiece[] {
        return this.textPieces.filter((piece) => !piece.isDeleted && piece.endOffset > piece.startOffset);
    }

    /**
     * The text as it stood before `author` touched it: the author's insertions
     * are dropped and the author's deletions kept, while every other author's
     * changes are taken as accepted. This is the "original" side of the
     * three-way merge: what the document said when the redline was drafted, as
     * far as this redline is concerned.
     */
    textBeforeAuthor(author: string): string {
        return this.textPieces
            .filter((piece) => !Revision.sameAuthor(piece.insertedBy, author) && (!piece.isDeleted || Revision.sameAuthor(piece.deletedBy, author)))
            .map((piece) => piece.text)
            .join("");
    }

    /**
     * The paragraph with every tracked change still visible, in the compact
     * form the edit model is shown: insertions wrapped in `<ins author="…">`,
     * deletions in `<del author="…">`, insertion outermost so a deletion made
     * inside someone else's insertion reads `<ins author="A">kept <del
     * author="B">struck</del></ins>`, the same nesting Word shows.
     *
     * Pieces are grouped twice: first into insertion groups, one per run of
     * pieces sharing an inserting author (or sharing "no inserter"), then each
     * group into deletion segments sharing a deleting author. Authors compare
     * through `Revision.sameAuthor`, so Word's two spellings of one person
     * never split a run. Text is XML-escaped and a line break becomes `<br/>`,
     * so the result drops straight into the core's `<paragraph>` element.
     */
    trackedChangesText(): string {
        return this.promptText({ agentView: false });
    }

    /**
     * The agent's reading of the paragraph: `trackedChangesText` plus the three
     * markers prod's agent view adds. Struck runs read `<strike>…</strike>`,
     * underlined runs `<u>…</u>` (strike outermost when both apply), and a
     * picture or embedded object reads `<image/>` where it sits. The markers
     * describe how existing text is displayed; an anchor quotes the text
     * without them.
     */
    agentViewText(): string {
        return this.promptText({ agentView: true });
    }

    /**
     * One entry of the paragraph in reading order: a text piece, or an inline
     * object marker placed before the piece it precedes. Image markers are
     * always listed, whichever view is rendered, so both views group and number
     * the paragraph's changes alike; the engine's view leaves them unrendered.
     */
    private promptItems(): PromptItem[] {
        const items: PromptItem[] = [];
        /**
         * EXAMPLE
         *   pieces  = [A, B]
         *   markers = [{beforePieceIndex: 1}, {beforePieceIndex: 2}]
         *   order   -> A, <image/>, B, <image/>
         *
         * WHY
         *   `beforePieceIndex === pieces.length` is the marker that closes the paragraph, so the
         *   loop runs one step past the last piece to flush it.
         */
        for (let pieceIndex = 0; pieceIndex <= this.textPieces.length; pieceIndex += 1) {
            for (const marker of this.inlineObjects) {
                // A marker is emitted at the step of the piece it precedes, so it keeps its place in the text.
                if (marker.beforePieceIndex === pieceIndex) items.push({ kind: "image", insertedBy: marker.insertedBy, deletedBy: marker.deletedBy });
            }
            /**
             * WHEN the loop is past the last piece
             *   CONSEQUENCE
             *     only the closing markers were due; nothing more to add.
             */
            if (pieceIndex === this.textPieces.length) break;
            const piece = this.textPieces[pieceIndex];
            items.push({ kind: "text", piece, insertedBy: piece.insertedBy, deletedBy: piece.deletedBy });
        }
        return items;
    }

    /**
     * The paragraph's pending tracked changes as the reader numbers them: every
     * insertion group and every deletion segment the prompt text wraps in a tag,
     * in document order, an insertion before the deletions nested in it, so the
     * id the agent quotes back names one of these.
     *
     * EXAMPLE
     *   `<ins id="c1" author="A">kept <del id="c2" author="B">struck</del></ins> <del id="c3" author="B">gone</del>`
     *   -> [c1: ins by A over "kept struck", c2: del by B over "struck", c3: del by B over "gone"]
     *
     * RETURNS
     *   [] for a paragraph with no tracked change.
     */
    pendingChanges(): PendingChange[] {
        const changes: PendingChange[] = [];
        for (const group of this.changeGroups()) {
            const textPiecesOf = (segments: DeletionSegment[]): TextPiece[] =>
                segments.flatMap((segment) => segment.items.flatMap((item) => (item.kind === "text" ? [item.piece] : [])));
            /**
             * WHEN an author inserted the whole group
             *   CONSEQUENCE
             *     the group is one change, listed before the deletions nested in it, as its id orders it.
             *     Its date is the insertion container of its first text piece (`pieces[0]`).
             */
            if (group.changeId !== null && group.insertedBy !== null) {
                const insertedPieces = textPiecesOf(group.segments);
                changes.push({ id: group.changeId, kind: "ins", author: group.insertedBy, date: ParagraphModel.revisionDateOf(insertedPieces[0]?.insertionContainer ?? null), pieces: insertedPieces });
            }
            for (const segment of group.segments) {
                /**
                 * WHEN an author struck the segment
                 *   CONSEQUENCE
                 *     the segment is one change, dated by the deletion container of its first text piece (`pieces[0]`).
                 */
                if (segment.changeId !== null && segment.deletedBy !== null) {
                    const deletedPieces = textPiecesOf([segment]);
                    changes.push({ id: segment.changeId, kind: "del", author: segment.deletedBy, date: ParagraphModel.revisionDateOf(deletedPieces[0]?.deletionContainer ?? null), pieces: deletedPieces });
                }
            }
        }
        return changes;
    }

    /**
     * The paragraph's tracked formatting changes, one per stretch of live text whose
     * run carries a `w:rPrChange`, in document order: who changed how the text looks,
     * when, and over which words. Struck text is left out, since its formatting no
     * longer shows.
     *
     * EXAMPLE
     *   `<w:r><w:rPr><w:b/><w:rPrChange w:author="Felix" w:date="2026-09-25T11:00:00Z"><w:rPr/></w:rPrChange></w:rPr><w:t>Term</w:t></w:r>`
     *   -> [{ author: "Felix", date: "2026-09-25T11:00:00Z", text: "Term", startOffset: 0, endOffset: 4 }]
     *
     * RETURNS
     *   [] for a paragraph with no tracked formatting change.
     */
    formattingChanges(): FormattingChange[] {
        const changes: FormattingChange[] = [];
        let previousPropertyChange: Element | null = null;
        for (const piece of this.textPieces) {
            const propertyChange = piece.isDeleted ? null : ParagraphModel.runPropertyChangeOf(piece.run);
            /**
             * WHEN the piece is struck, or its run carries no property change
             *   CONSEQUENCE
             *     it belongs to no formatting change, and the next one opens a new change.
             */
            if (propertyChange === null) {
                previousPropertyChange = null;
                continue;
            }
            /**
             * WHEN the piece shares the previous piece's property change (one run split into several text nodes)
             *   CONSEQUENCE
             *     extend the open change, the last one listed.
             *
             * EXAMPLE
             *   changes.length = 2
             *   length - 1     -> 1, the change the previous piece opened
             */
            if (propertyChange === previousPropertyChange) {
                const openChange = changes[changes.length - 1];
                openChange.text += piece.text;
                openChange.endOffset = piece.endOffset;
                continue;
            }
            changes.push({
                author: propertyChange.getAttributeNS(WORDPROCESSINGML_NS, "author") ?? propertyChange.getAttribute("w:author") ?? "",
                date: ParagraphModel.revisionDateOf(propertyChange),
                text: piece.text,
                startOffset: piece.startOffset,
                endOffset: piece.endOffset,
            });
            previousPropertyChange = propertyChange;
        }
        return changes;
    }

    /**
     * A revision element's `w:date`, for a tracked change container or a property change.
     *
     * CASES
     *   - an element with a non-empty `w:date` -> that string, as Word wrote it
     *   - no element, or no date on it         -> null
     */
    private static revisionDateOf(revisionElement: Element | null): string | null {
        /**
         * WHEN there is no revision element (a change that holds only a picture)
         *   CONSEQUENCE
         *     no date to read.
         */
        if (revisionElement === null) return null;
        const date = revisionElement.getAttributeNS(WORDPROCESSINGML_NS, "date") ?? revisionElement.getAttribute("w:date");
        /**
         * CASES
         *   - `w:date` absent or empty -> null: Word drops it when personal information is removed
         *   - otherwise                -> the date as written
         */
        return date === null || date === "" ? null : date;
    }

    /**
     * The `w:rPrChange` in a run's own properties: the record that someone changed how
     * the run looks, holding the properties it had before.
     *
     * NULL
     *   The run has no properties block, or its properties carry no tracked change.
     */
    private static runPropertyChangeOf(run: Element): Element | null {
        const runProperties = Array.from(run.children).find((child) => elementNameWithoutPrefix(child) === "rPr");
        /**
         * WHEN the run has no properties block
         *   CONSEQUENCE
         *     it has no property change either.
         */
        if (runProperties === undefined) return null;
        return Array.from(runProperties.children).find((child) => elementNameWithoutPrefix(child) === "rPrChange") ?? null;
    }

    /**
     * The paragraph's items grouped the way the prompt text wraps them, each
     * group and segment that is a tracked change carrying its id: "c1", "c2", …
     * in document order, an insertion group numbered before the deletion
     * segments inside it, so the ids read in the order the tags open.
     *
     * EXAMPLE
     *   items   = kept(ins A) struck(ins A, del B) gone(del B)
     *   groups  -> [ins A: [live "kept" | del B "struck"]], [original: [del B "gone"]]
     *   ids     -> the A group c1, its del segment c2, the last del segment c3
     */
    private changeGroups(): InsertionGroup[] {
        const insertionGroups: InsertionGroup[] = [];
        for (const piece of this.promptItems()) {
            /**
             * EXAMPLE
             *   `insertionGroups.length` = 2
             *   `length - 1`             -> 1, the group being extended
             *
             * CASES
             *   - no group yet                       -> open one
             *   - same inserting author as the last  -> extend it
             *   - different inserting author         -> open one
             */
            const groupBeingExtended = insertionGroups.length > 0 ? insertionGroups[insertionGroups.length - 1] : null;
            const openGroup = groupBeingExtended !== null && ParagraphModel.sameAuthorOrBothAbsent(groupBeingExtended.insertedBy, piece.insertedBy) ? groupBeingExtended : null;
            const group: InsertionGroup = openGroup ?? { insertedBy: piece.insertedBy, changeId: null, segments: [] };
            /**
             * WHEN this piece opened a new insertion group
             *   CONSEQUENCE
             *     append the group so later pieces can extend it.
             */
            if (openGroup === null) insertionGroups.push(group);
            /**
             * EXAMPLE
             *   `group.segments.length` = 1
             *   `length - 1`            -> 0, the segment being extended
             *
             * CASES
             *   - same deleting author as the last segment -> append text to it
             *   - otherwise                                -> open a new segment
             */
            const segmentBeingExtended = group.segments.length > 0 ? group.segments[group.segments.length - 1] : null;
            const segment: DeletionSegment = segmentBeingExtended !== null && ParagraphModel.sameAuthorOrBothAbsent(segmentBeingExtended.deletedBy, piece.deletedBy)
                ? segmentBeingExtended
                : { deletedBy: piece.deletedBy, changeId: null, items: [] };
            /**
             * WHEN this piece opened a new deletion segment
             *   CONSEQUENCE
             *     append the segment so later pieces can extend it.
             */
            if (segment !== segmentBeingExtended) group.segments.push(segment);
            segment.items.push(piece);
        }
        /**
         * EXAMPLE
         *   changeCount before an inserted group = 0 -> the group reads c1, and 0 + 1 = 1 changes are numbered
         *   its first struck segment               -> c2
         */
        let changeCount = 0;
        const nextChangeId = (): string => {
            changeCount += 1;
            return `c${changeCount}`;
        };
        for (const group of insertionGroups) {
            if (group.insertedBy !== null) group.changeId = nextChangeId();
            for (const segment of group.segments) {
                if (segment.deletedBy !== null) segment.changeId = nextChangeId();
            }
        }
        return insertionGroups;
    }

    private promptText({ agentView }: { agentView: boolean }): string {
        return this.changeGroups().map((group) => ParagraphModel.renderInsertionGroup(group, agentView)).join("");
    }

    /**
     * The runs one segment renders as: an image marker as its own run, text
     * merged into the previous run when both carry the same formatting so a word
     * split across runs by Word still renders as one `<strike>` span.
     *
     * CASES
     *   - agent view  -> each piece keeps the run's real formatting, so the markers can be drawn, and an image is a run
     *   - engine text -> every piece is plain, so the pieces merge into one unformatted run, and an image is left out
     */
    private static segmentRuns(segment: DeletionSegment, agentView: boolean): PromptRun[] {
        const runs: PromptRun[] = [];
        for (const item of segment.items) {
            /**
             * WHEN the item is an inline object
             *   CONSEQUENCE
             *     in the agent view it is its own run; markers never merge with text. The
             *     engine's text has no place for it.
             */
            if (item.kind === "image") {
                if (agentView) runs.push({ text: "<image/>", formatting: PLAIN_FORMATTING, isImage: true });
                continue;
            }
            const formatting = agentView ? item.piece.formatting : PLAIN_FORMATTING;
            /**
             * EXAMPLE
             *   runs.length = 2
             *   length - 1  -> 1, the run last appended, the only one text can merge into
             *   length = 0  -> null: the first run of the segment opens fresh
             */
            const runBeingExtended = runs.length > 0 ? runs[runs.length - 1] : null;
            const sameFormatting = runBeingExtended !== null && !runBeingExtended.isImage
                && runBeingExtended.formatting.struck === formatting.struck
                && runBeingExtended.formatting.underlined === formatting.underlined;
            /**
             * CASES
             *   - previous run is text with the same formatting -> extend it
             *   - otherwise                                      -> open a run
             */
            if (sameFormatting && runBeingExtended !== null) {
                runBeingExtended.text += item.piece.text;
                continue;
            }
            runs.push({ text: item.piece.text, formatting, isImage: false });
        }
        return runs;
    }

    /**
     * CASES
     *   - both null        -> same (original text, or live text)
     *   - exactly one null -> different
     *   - both present     -> `Revision.sameAuthor`
     */
    private static sameAuthorOrBothAbsent(knownAuthor: string | null, candidateAuthor: string | null): boolean {
        if (knownAuthor === null || candidateAuthor === null) return knownAuthor === candidateAuthor;
        return Revision.sameAuthor(knownAuthor, candidateAuthor);
    }

    /**
     * One insertion group as prompt text: its deletion segments rendered in
     * order, wrapped in `<ins>` carrying the change's id when an author inserted
     * the whole group.
     *
     * WHEN the group renders no text in this view (an image alone, in the engine's text)
     *   CONSEQUENCE
     *     nothing, not even the tag; its id stays taken, so the agent view and the
     *     engine text number the paragraph's changes alike.
     */
    private static renderInsertionGroup(group: InsertionGroup, agentView: boolean): string {
        const segmentsText = group.segments.map((segment) => ParagraphModel.renderDeletionSegment(segment, agentView)).join("");
        /**
         * WHEN the group is original text (nobody inserted it)
         *   CONSEQUENCE
         *     no wrapper; deletions inside it were already wrapped per segment.
         */
        if (group.insertedBy === null || segmentsText.length === 0) return segmentsText;
        return `<ins${ParagraphModel.changeIdAttribute(group.changeId)}${ParagraphModel.authorAttribute(group.insertedBy)}>${segmentsText}</ins>`;
    }

    /**
     * One deletion segment as prompt text: escaped, line breaks as `<br/>`,
     * wrapped in `<del>` carrying the change's id when an author struck it, and
     * nothing at all when it renders no text in this view.
     */
    private static renderDeletionSegment(segment: DeletionSegment, agentView: boolean): string {
        const segmentText = ParagraphModel.segmentRuns(segment, agentView).map((run) => ParagraphModel.renderRun(run)).join("");
        /**
         * WHEN the segment is live text (nobody deleted it), or renders nothing in this view
         *   CONSEQUENCE
         *     emit the text alone.
         */
        if (segment.deletedBy === null || segmentText.length === 0) return segmentText;
        return `<del${ParagraphModel.changeIdAttribute(segment.changeId)}${ParagraphModel.authorAttribute(segment.deletedBy)}>${segmentText}</del>`;
    }

    /** ` id="c3"` for a numbered change, nothing for a group or segment that is not a change. */
    private static changeIdAttribute(changeId: string | null): string {
        return changeId === null ? "" : ` id="${changeId}"`;
    }

    /**
     * One run as prompt text: an image marker verbatim; text escaped, then
     * wrapped `<strike><u>…</u></strike>` for what its formatting says, the
     * order prod's agent view uses so the two views read alike.
     */
    private static renderRun(run: PromptRun): string {
        /**
         * WHEN the run is an inline object marker
         *   CONSEQUENCE
         *     emit it as is; it is markup, not text to escape.
         */
        if (run.isImage) return run.text;
        let renderedText = ParagraphModel.escapePromptText(run.text);
        /**
         * WHEN the run is underlined
         *   CONSEQUENCE
         *     wrap in `<u>` first, so it sits inside a strike wrapper.
         */
        if (run.formatting.underlined) renderedText = `<u>${renderedText}</u>`;
        /**
         * WHEN the run is struck
         *   CONSEQUENCE
         *     wrap in `<strike>` outermost.
         */
        if (run.formatting.struck) renderedText = `<strike>${renderedText}</strike>`;
        return renderedText;
    }

    /**
     * ` author="…"` for a named author, nothing for a revision container that
     * carried no author. Double quotes inside a name become single quotes so
     * the attribute stays well-formed; `&` and `<` are escaped for the same
     * reason.
     */
    private static authorAttribute(author: string): string {
        /**
         * WHEN the revision container had no `w:author`
         *   CONSEQUENCE
         *     the tag carries no attribute, matching what the model was shown before.
         */
        if (author.length === 0) return "";
        const attributeSafeAuthor = author.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "'");
        return ` author="${attributeSafeAuthor}"`;
    }

    /**
     * Escapes the three characters that would break the prompt's XML, then
     * writes every line break as `<br/>`, which is how the model has always
     * seen a soft break inside a paragraph.
     */
    private static escapePromptText(text: string): string {
        return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>");
    }
}

/** What a pending tracked change is: words an author added, or words an author struck. */
export type PendingChangeKind = "ins" | "del";

/**
 * One pending tracked change of a paragraph, numbered as the reader shows it:
 * `id` is "c1", "c2", … in document order, an insertion before the deletions
 * nested in it; `kind` and `author` are what the tag says; `pieces` are the text
 * pieces inside the change, the deleted ones included for an insertion, so the
 * engine can revert exactly what the agent named.
 */
export interface PendingChange {
    id: string;
    kind: PendingChangeKind;
    author: string;
    /**
     * When the change was made: the `w:date` of the revision container holding the
     * change's first text piece, as Word wrote it. A change that spans two adjacent
     * revisions by one author reads the first one's date.
     *
     * NULL
     *   The container carries no date (Word drops dates when personal information is
     *   removed), or the change holds no text, such as an inserted picture.
     */
    date: string | null;
    pieces: TextPiece[];
}

/**
 * One tracked formatting change: who changed how a stretch of live text looks, and
 * when. `startOffset` and `endOffset` index the accepted text, end exclusive, so
 * `text === allChangesAcceptedText.slice(startOffset, endOffset)`.
 */
export interface FormattingChange {
    author: string;
    /**
     * The `w:rPrChange`'s `w:date`.
     *
     * NULL
     *   The change carries no date (personal information removed).
     */
    date: string | null;
    text: string;
    startOffset: number;
    endOffset: number;
}

/** A run of items sharing one inserting author (null for original text), split into deletion segments; `changeId` names it when an author inserted it. */
interface InsertionGroup {
    insertedBy: string | null;
    changeId: string | null;
    segments: DeletionSegment[];
}

/** A run of items inside one insertion group sharing one deleting author (null while the text is live); `changeId` names it when an author struck it. */
interface DeletionSegment {
    deletedBy: string | null;
    changeId: string | null;
    items: PromptItem[];
}

/** Consecutive text sharing one formatting inside a deletion segment, or one inline object marker. */
interface PromptRun {
    text: string;
    formatting: RunFormatting;
    isImage: boolean;
}

/** One entry of the paragraph in reading order, as the grouping consumes it: a text piece, or an inline object marker. */
type PromptItem =
    | { kind: "text"; piece: TextPiece; insertedBy: string | null; deletedBy: string | null }
    | { kind: "image"; insertedBy: string | null; deletedBy: string | null };
